import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip as LeafletTooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { brokers } from '../services/api.js';
import { useAuth } from '../auth/AuthContext.jsx';
import './BrokerDashboard.css';

/* Fallback sample data shown when brokers don't have premium data yet */
const SAMPLE_RETAIL_BROKERS = [
  { name: 'Alliant', premium: 3600000 },
  { name: 'Leavitt', premium: 2900000 },
  { name: 'Patriot Growth', premium: 2400000 },
  { name: 'IMA Financial', premium: 1800000 },
  { name: 'Hilb', premium: 1500000 },
];

const SAMPLE_WHOLESALE_BROKERS = [
  { name: 'CRC Group', premium: 4200000 },
  { name: 'Amwins', premium: 3800000 },
  { name: 'RT Specialty', premium: 3100000 },
  { name: 'Burns & Wilcox', premium: 2100000 },
  { name: 'RPS', premium: 1700000 },
];

/* Hex colors for broker type pins (Leaflet needs real color values) */
const TYPE_COLORS = {
  wholesale: '#3b82f6',
  retail: '#16a34a',
  aggregator: '#7c3aed',
};

/* Lighter versions for cluster fills */
const TYPE_COLORS_LIGHT = {
  wholesale: 'rgba(59, 130, 246, 0.7)',
  retail: 'rgba(22, 163, 74, 0.7)',
  aggregator: 'rgba(124, 58, 237, 0.7)',
};
/* State centroids [lat, lng] for Leaflet */
const STATE_CENTROIDS = {
  AL: [32.8, -86.9], AK: [64.3, -153.5], AZ: [34.3, -111.7], AR: [34.9, -92.4],
  CA: [36.8, -119.4], CO: [39.0, -105.5], CT: [41.6, -72.7], DE: [39.0, -75.5],
  FL: [27.6, -81.5], GA: [32.2, -83.5], HI: [19.9, -155.5], ID: [44.1, -114.7],
  IL: [40.6, -89.4], IN: [39.8, -86.1], IA: [42.0, -93.5], KS: [38.5, -98.3],
  KY: [37.8, -84.3], LA: [30.5, -91.9], ME: [45.3, -69.4], MD: [39.0, -76.6],
  MA: [42.4, -71.1], MI: [44.3, -84.5], MN: [46.4, -94.6], MS: [32.7, -89.7],
  MO: [38.6, -91.8], MT: [46.9, -109.6], NE: [41.5, -99.9], NV: [38.8, -116.4],
  NH: [43.2, -71.6], NJ: [40.1, -74.4], NM: [34.5, -105.9], NY: [40.7, -74.0],
  NC: [35.8, -79.0], ND: [47.5, -100.5], OH: [40.4, -82.9], OK: [35.5, -97.5],
  OR: [43.8, -120.6], PA: [41.2, -77.2], RI: [41.7, -71.5], SC: [34.0, -81.0],
  SD: [44.4, -99.9], TN: [35.5, -86.6], TX: [31.0, -97.7], UT: [39.3, -111.9],
  VT: [44.0, -72.6], VA: [37.4, -79.5], WA: [47.4, -120.7], WV: [38.6, -80.6],
  WI: [43.8, -89.6], WY: [43.0, -107.6], DC: [38.9, -77.0],
};

/* Jitter individual broker pins within a state so they don't stack (lat/lng order) */
function jitterCoords(baseLat, baseLng, index, total) {
  if (total <= 1) return [baseLat, baseLng];
  const angle = (2 * Math.PI * index) / Math.min(total, 12) + (index >= 12 ? 0.3 : 0);
  const radius = 0.5 + (index >= 12 ? 0.8 : 0);
  return [baseLat + radius * Math.sin(angle), baseLng + radius * Math.cos(angle)];
}

/* Helper: fly the Leaflet map to a new view */
function MapController({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    map.flyTo(center, zoom, { duration: 1.2 });
  }, [map, center, zoom]);
  return null;
}

function formatCurrency(val) {
  if (val == null) return '—';
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
  return `$${val}`;
}

function formatChartCurrency(val) {
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
  return `$${val}`;
}

const EMPTY_FORM = {
  Name: '',
  Type: 'wholesale',
  Website: '',
  Phone: '',
  Address: '',
  City: '',
  State: '',
  ZipCode: '',
};

export function BrokerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [brokerList, setBrokerList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterState, setFilterState] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [selectedState, setSelectedState] = useState(null);

  const loadBrokers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await brokers.list();
      const data = result.data || result;
      setBrokerList(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load brokers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBrokers();
  }, [loadBrokers]);

  const availableStates = [...new Set(brokerList.map(b => b.State || b.state).filter(Boolean))].sort();

  /* Compute top brokers by premium from real data, fall back to sample data */
  const topWholesaleBrokers = useMemo(() => {
    const real = brokerList
      .filter(b => (b.Type || b.brokerType || '').toLowerCase() === 'wholesale' && (b.TotalPremium || b.totalPremium))
      .map(b => ({ name: b.Name || b.name || 'Unknown', premium: b.TotalPremium || b.totalPremium || 0 }))
      .sort((a, b) => b.premium - a.premium)
      .slice(0, 5);
    return real.length > 0 ? real : SAMPLE_WHOLESALE_BROKERS;
  }, [brokerList]);

  const topRetailBrokers = useMemo(() => {
    const real = brokerList
      .filter(b => (b.Type || b.brokerType || '').toLowerCase() === 'retail' && (b.TotalPremium || b.totalPremium))
      .map(b => ({ name: b.Name || b.name || 'Unknown', premium: b.TotalPremium || b.totalPremium || 0 }))
      .sort((a, b) => b.premium - a.premium)
      .slice(0, 5);
    return real.length > 0 ? real : SAMPLE_RETAIL_BROKERS;
  }, [brokerList]);

  const usingRealPremiumData = useMemo(() => {
    return brokerList.some(b => b.TotalPremium || b.totalPremium);
  }, [brokerList]);

  const filtered = brokerList.filter((b) => {
    const bType = b.Type || b.brokerType || 'wholesale';
    if (filterType && bType !== filterType) return false;
    const bState = b.State || b.state;
    if (filterState && bState !== filterState) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (
        !(b.Name || b.name || '').toLowerCase().includes(q) &&
        !(b.ContactEmail || b.contactEmail || b.Website || b.website || '').toLowerCase().includes(q) &&
        !(b.MasterCode || b.masterCode || '').toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });

  /* Compute map markers from live broker data, grouped by state */
  const geoMarkers = useMemo(() => {
    const stateCounts = {};
    filtered.forEach(b => {
      const st = (b.State || b.state || '').toUpperCase();
      if (st && STATE_CENTROIDS[st]) {
        stateCounts[st] = (stateCounts[st] || 0) + 1;
      }
    });
    return Object.entries(stateCounts)
      .map(([state, count]) => ({ state, count, coords: STATE_CENTROIDS[state] }))
      .sort((a, b) => b.count - a.count);
  }, [filtered]);

  /* Brokers in the selected state for drill-down view */
  const stateBrokers = useMemo(() => {
    if (!selectedState) return [];
    return filtered.filter(b => {
      const st = (b.State || b.state || '').toUpperCase();
      return st === selectedState;
    });
  }, [filtered, selectedState]);

  /* Map view config — zoom into state when selected */
  const mapView = useMemo(() => {
    if (!selectedState || !STATE_CENTROIDS[selectedState]) {
      return { center: [39.5, -98.35], zoom: 4 };
    }
    return { center: STATE_CENTROIDS[selectedState], zoom: 7 };
  }, [selectedState]);

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      await brokers.create({
        name: formData.Name,
        brokerType: formData.Type,
        website: formData.Website,
        phone: formData.Phone,
        address: formData.Address,
        city: formData.City,
        state: formData.State,
        zipCode: formData.ZipCode,
      });
      setShowAddForm(false);
      setFormData({ ...EMPTY_FORM });
      await loadBrokers();
    } catch (err) {
      setSaveError(err.message || 'Failed to create broker');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bd-loading">
        <div className="spinner" />
        <p>Loading brokers...</p>
      </div>
    );
  }

  if (error && brokerList.length === 0) {
    return (
      <div className="bd-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button className="btn btn--primary" onClick={loadBrokers}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bd">
      {/* Header */}
      <div className="bd__header">
        <h1 className="bd__title">Brokers</h1>
        <button
          className="btn btn--primary"
          onClick={() => setShowAddForm(true)}
        >
          + Add Broker
        </button>
      </div>

      {/* Add Broker Modal */}
      {showAddForm && (
        <div className="bd__modal-overlay" onClick={() => setShowAddForm(false)}>
          <div className="bd__modal card" onClick={(e) => e.stopPropagation()}>
            <div className="bd__modal-header">
              <h2 className="bd__modal-title">Add New Broker</h2>
              <button
                className="bd__modal-close"
                onClick={() => setShowAddForm(false)}
              >
                &times;
              </button>
            </div>
            <form className="bd__form" onSubmit={handleCreate}>
              <div className="bd__form-grid">
                <div className="bd__form-field">
                  <label className="bd__form-label">Name *</label>
                  <input
                    className="bd__form-input"
                    type="text"
                    required
                    value={formData.Name}
                    onChange={(e) => handleFormChange('Name', e.target.value)}
                    placeholder="Broker name"
                  />
                </div>
                <div className="bd__form-field">
                  <label className="bd__form-label">Type *</label>
                  <select
                    className="bd__form-input"
                    value={formData.Type}
                    onChange={(e) => handleFormChange('Type', e.target.value)}
                  >
                    <option value="wholesale">Wholesale</option>
                    <option value="retail">Retail</option>
                    <option value="aggregator">Aggregator/Network</option>
                  </select>
                </div>
                <div className="bd__form-field">
                  <label className="bd__form-label">Website *</label>
                  <input
                    className="bd__form-input"
                    type="url"
                    required
                    value={formData.Website}
                    onChange={(e) =>
                      handleFormChange('Website', e.target.value)
                    }
                    placeholder="https://www.example.com"
                  />
                </div>
                <div className="bd__form-field">
                  <label className="bd__form-label">Phone *</label>
                  <input
                    className="bd__form-input"
                    type="tel"
                    required
                    value={formData.Phone}
                    onChange={(e) => handleFormChange('Phone', e.target.value)}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="bd__form-field">
                  <label className="bd__form-label">Address *</label>
                  <input
                    className="bd__form-input"
                    type="text"
                    required
                    value={formData.Address}
                    onChange={(e) => handleFormChange('Address', e.target.value)}
                    placeholder="Street address"
                  />
                </div>
                <div className="bd__form-field">
                  <label className="bd__form-label">City *</label>
                  <input
                    className="bd__form-input"
                    type="text"
                    required
                    value={formData.City}
                    onChange={(e) => handleFormChange('City', e.target.value)}
                    placeholder="City"
                  />
                </div>
                <div className="bd__form-field">
                  <label className="bd__form-label">State *</label>
                  <input
                    className="bd__form-input"
                    type="text"
                    required
                    maxLength={2}
                    value={formData.State}
                    onChange={(e) =>
                      handleFormChange('State', e.target.value.toUpperCase())
                    }
                    placeholder="CA"
                  />
                </div>
                <div className="bd__form-field">
                  <label className="bd__form-label">Zip Code *</label>
                  <input
                    className="bd__form-input"
                    type="text"
                    required
                    value={formData.ZipCode}
                    onChange={(e) => handleFormChange('ZipCode', e.target.value)}
                    placeholder="90210"
                  />
                </div>
              </div>
              {saveError && <p className="bd__form-error">{saveError}</p>}
              <div className="bd__form-actions">
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => setShowAddForm(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Create Broker'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filters bar */}
      <div className="bd__filters">
        <div className="bd__search-wrap bd__filter-search">
          <input
            className="bd__search"
            type="text"
            placeholder="Search brokers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="bd__search-clear" onClick={() => setSearch('')}>
              &times;
            </button>
          )}
        </div>
        <select
          className="bd__filter-select"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="">All Types</option>
          <option value="retail">Retail</option>
          <option value="wholesale">Wholesale</option>
          <option value="aggregator">Aggregator</option>
        </select>
        <select
          className="bd__filter-select"
          value={filterState}
          onChange={(e) => setFilterState(e.target.value)}
        >
          <option value="">All States</option>
          {availableStates.map(st => (
            <option key={st} value={st}>{st}</option>
          ))}
        </select>
      </div>

      {/* Top Brokers — Retail vs Wholesale side-by-side, filtered */}
      <div className="bd__section">
        <h2 className="bd__section-title">Top Brokers by Premium</h2>
        {!usingRealPremiumData && (
          <p className="bd__sample-note">Showing illustrative data — premium tracking will populate as policies are bound.</p>
        )}
        <div className={`bd__charts-row${filterType === 'wholesale' || filterType === 'retail' ? ' bd__charts-row--single' : ''}`}>
          {filterType !== 'retail' && (
          <div className="bd__chart-card card">
            <h3 className="bd__chart-subtitle">Wholesale</h3>
            <div style={{ minWidth: 320, width: '100%' }}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart
                data={topWholesaleBrokers}
                margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
                  tickLine={false}
                  axisLine={{ stroke: 'var(--color-border)' }}
                  interval={0}
                />
                <YAxis
                  tickFormatter={formatChartCurrency}
                  tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
                  tickLine={false}
                  axisLine={{ stroke: 'var(--color-border)' }}
                  width={50}
                />
                <Tooltip
                  formatter={(value) => [formatCurrency(value), 'Premium']}
                  contentStyle={{
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 13,
                  }}
                />
                <Bar
                  dataKey="premium"
                  fill="var(--color-primary)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
            </div>
          </div>
          )}
          {filterType !== 'wholesale' && (
          <div className="bd__chart-card card">
            <h3 className="bd__chart-subtitle">Retail</h3>
            <div style={{ minWidth: 320, width: '100%' }}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart
                data={topRetailBrokers}
                margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
                  tickLine={false}
                  axisLine={{ stroke: 'var(--color-border)' }}
                  interval={0}
                />
                <YAxis
                  tickFormatter={formatChartCurrency}
                  tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
                  tickLine={false}
                  axisLine={{ stroke: 'var(--color-border)' }}
                  width={50}
                />
                <Tooltip
                  formatter={(value) => [formatCurrency(value), 'Premium']}
                  contentStyle={{
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 13,
                  }}
                />
                <Bar
                  dataKey="premium"
                  fill="var(--color-risk-green)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Geographic Distribution — US Map */}
      <div className="bd__section">
        <div className="bd__map-header">
          <h2 className="bd__section-title">
            Geographic Distribution
            {selectedState && (
              <span className="bd__map-state-label"> — {selectedState}</span>
            )}
          </h2>
          {selectedState && (
            <button
              className="bd__map-back-btn"
              onClick={() => setSelectedState(null)}
            >
              ← Back to US
            </button>
          )}
        </div>
        <div className="bd__map-card card">
          {/* Map type legend */}
          <div className="bd__map-legend">
            <span className="bd__map-legend-item">
              <span className="bd__map-legend-dot" style={{ background: '#3b82f6' }} />
              Wholesale
            </span>
            <span className="bd__map-legend-item">
              <span className="bd__map-legend-dot" style={{ background: '#16a34a' }} />
              Retail
            </span>
            <span className="bd__map-legend-item">
              <span className="bd__map-legend-dot" style={{ background: '#7c3aed' }} />
              Aggregator
            </span>
            <span className="bd__map-legend-count">
              {geoMarkers.reduce((s, m) => s + m.count, 0)} brokers across {geoMarkers.length} states
            </span>
          </div>
          <div className="bd__map-leaflet">
            <MapContainer
              center={[39.5, -98.35]}
              zoom={4}
              scrollWheelZoom={true}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              />
              <MapController center={mapView.center} zoom={mapView.zoom} />

              {/* National view: state-level cluster markers */}
              {!selectedState && geoMarkers.map(({ state, count, coords }) => (
                <CircleMarker
                  key={state}
                  center={coords}
                  radius={10}
                  pathOptions={{
                    fillColor: '#3b82f6',
                    fillOpacity: 0.7,
                    color: 'white',
                    weight: 2,
                  }}
                  eventHandlers={{
                    click: () => setSelectedState(state),
                  }}
                >
                  <LeafletTooltip direction="top" permanent className="bd__map-cluster-label">
                    {state} ({count})
                  </LeafletTooltip>
                </CircleMarker>
              ))}

              {/* State drill-down: individual broker pins */}
              {selectedState && stateBrokers.map((b, idx) => {
                const base = STATE_CENTROIDS[selectedState];
                if (!base) return null;
                const coords = jitterCoords(base[0], base[1], idx, stateBrokers.length);
                const bType = b.Type || b.brokerType || 'wholesale';
                const bName = b.Name || b.name || 'Unknown';
                const bCity = b.City || b.city || '';
                return (
                  <CircleMarker
                    key={b.BrokerID || b.brokerId || idx}
                    center={coords}
                    radius={8}
                    pathOptions={{
                      fillColor: TYPE_COLORS[bType] || TYPE_COLORS.wholesale,
                      fillOpacity: 0.9,
                      color: 'white',
                      weight: 2,
                    }}
                    eventHandlers={{
                      click: () => navigate(`/brokers/${b.BrokerID || b.brokerId}`),
                    }}
                  >
                    <LeafletTooltip direction="top">
                      <strong>{bName}</strong>
                      {bCity && <><br />{bCity}, {selectedState}</>}
                      <br /><em style={{ textTransform: 'capitalize' }}>{bType}</em>
                    </LeafletTooltip>
                    <Popup>
                      <strong>{bName}</strong>
                      {bCity && <><br />{bCity}, {selectedState}</>}
                      <br /><em style={{ textTransform: 'capitalize' }}>{bType}</em>
                      <br /><a href={`/brokers/${b.BrokerID || b.brokerId}`}>View Details →</a>
                    </Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          </div>
        </div>
      </div>

      {/* Broker List Table */}
      <div className="bd__section">
        <h2 className="bd__section-title">
          All Brokers
          <span className="bd__count">{filtered.length}</span>
        </h2>
        <div className="bd__table-wrap card">
          <table className="bd__table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Master Code</th>
                <th>Location</th>
                <th>Website</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length > 0 ? (
                filtered.map((b) => (
                  <tr
                    key={b.BrokerID}
                    className="bd__table-row"
                    onClick={() => navigate(`/brokers/${b.BrokerID}`)}
                  >
                    <td className="bd__table-name">{b.Name || '—'}</td>
                    <td>
                      <span
                        className={`bd__type-badge bd__type-badge--${b.Type || 'wholesale'}`}
                      >
                        {b.Type || '—'}
                      </span>
                    </td>
                    <td className="bd__table-code">{b.MasterCode || '—'}</td>
                    <td>
                      {[b.City, b.State].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className="bd__table-email">
                      {b.Website ? (
                        <a href={b.Website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                          {b.Website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                        </a>
                      ) : '—'}
                    </td>
                    <td>
                      <span
                        className={`bd__status-badge ${b.Active !== false ? 'bd__status-badge--active' : 'bd__status-badge--inactive'}`}
                      >
                        {b.Active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="bd__table-empty">
                    {search
                      ? 'No brokers match your search.'
                      : 'No brokers found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
